import { Accordion, Alert, AlertIcon, Box, Flex, Heading, Spinner, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetExclusionGroundsTaxonomyQuery,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { ExclusionGroundsCategoryGroup } from "./ExclusionGroundsCategoryGroup";
import { ExclusionGroundsSummary } from "./ExclusionGroundsSummary";

// t('Exclusion grounds') t('Exclusion grounds info') t('Failed to load exclusion grounds taxonomy')

interface Props {
  eventId: string;
}

export const ExclusionGroundsSection = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { data: taxonomy, isLoading, error } = useGetExclusionGroundsTaxonomyQuery();
  const { triggerSoft } = useApiError();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);

  useEffect(() => {
    if (error) triggerSoft(error);
  }, [error, triggerSoft]);

  const activeIds = useMemo(
    () => [...state.publicContext.activeExclusionGroundIds],
    [state.publicContext.activeExclusionGroundIds],
  );
  const activeIdSet = useMemo(() => new Set(activeIds), [activeIds]);
  const saveStatus = fieldStatus("publicContext.activeExclusionGroundIds");
  const isSaving = saveStatus === "saving" || saveStatus === "pending";

  const stats = useMemo(() => {
    if (!taxonomy) return { mandatoryCount: 0, voluntaryActiveCount: 0, voluntaryTotalCount: 0 };
    let mandatoryCount = 0;
    let voluntaryTotal = 0;
    let voluntaryActive = 0;
    for (const cat of taxonomy.categories) {
      for (const c of cat.criteria) {
        if (c.mandatory) {
          mandatoryCount++;
        } else {
          voluntaryTotal++;
          if (activeIdSet.has(c.id)) voluntaryActive++;
        }
      }
    }
    return { mandatoryCount, voluntaryActiveCount: voluntaryActive, voluntaryTotalCount: voluntaryTotal };
  }, [taxonomy, activeIdSet]);

  const handleToggleCriterion = useCallback(
    (id: string, active: boolean) => {
      const newIds = active
        ? [...new Set([...activeIds, id])]
        : activeIds.filter((x) => x !== id);
      updateField({ publicContext: { activeExclusionGroundIds: newIds } });
    },
    [activeIds, updateField],
  );

  const handleToggleCategory = useCallback(
    (categoryName: string, active: boolean) => {
      if (!taxonomy) return;
      const category = taxonomy.categories.find((c) => c.code === categoryName);
      if (!category) return;
      const voluntaryIdsInCategory = category.criteria.filter((c) => !c.mandatory).map((c) => c.id);
      const newIds = active
        ? [...new Set([...activeIds, ...voluntaryIdsInCategory])]
        : activeIds.filter((id) => !voluntaryIdsInCategory.includes(id));
      updateField({ publicContext: { activeExclusionGroundIds: newIds } });
    },
    [activeIds, taxonomy, updateField],
  );

  if (isLoading) {
    return (
      <Box py="8">
        <Spinner />
      </Box>
    );
  }

  if (!taxonomy) {
    return (
      <Box pt="10" pb="4">
        <Heading as="h2" size="md" mb="4">
          {t("Exclusion grounds")}
        </Heading>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <Text fontSize="sm">{t("Failed to load exclusion grounds taxonomy")}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt="10" pb="4">
      <Flex align="center" mb="4" gap="2">
        <Heading as="h2" size="md">
          {t("Exclusion grounds")}
        </Heading>
        {isSaving && <Spinner size="xs" />}
      </Flex>

      <Alert status="info" borderRadius="lg" mb="6">
        <AlertIcon />
        <Text fontSize="sm">{t("Exclusion grounds info")}</Text>
      </Alert>

      <ExclusionGroundsSummary
        mandatoryCount={stats.mandatoryCount}
        voluntaryActiveCount={stats.voluntaryActiveCount}
        voluntaryTotalCount={stats.voluntaryTotalCount}
      />

      <Accordion allowMultiple>
        {taxonomy.categories.map((category) => (
          <ExclusionGroundsCategoryGroup
            key={category.code}
            category={category}
            activeIds={activeIdSet}
            onToggleCriterion={handleToggleCriterion}
            onToggleCategory={handleToggleCategory}
          />
        ))}
      </Accordion>
    </Box>
  );
};
